import { Link } from "react-router-dom";
import { ArrowUpRight, PlusCircle } from "lucide-react";
import { MANUFACTURERS, PRODUCTS, IMAGES } from "../lib/data";
import { PageHero, SectionHead, CTABand } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";

import { Seo } from "../components/site/Seo";

export default function Manufacturers() {
  return (
    <div data-testid="manufacturers-page">
      <Seo title="Our Manufacturers" description="OneHarvest partners with best-in-class manufacturers to deliver proven industrial products and jobsite solutions." />
      <PageHero
        kicker="Manufacturers"
        title="We partner with the brands we trust."
        subtitle="We represent a curated set of manufacturers whose technologies are best-in-class for their application — and we're always evaluating the next one."
        image={IMAGES.offshore}
      />

      <section className="bg-background">
        <div className="container-x py-24">
          <SectionHead kicker="Trusted Partners" title="The manufacturers behind our portfolio" desc="Each brand earns its place through proven performance and reliability in the field." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {MANUFACTURERS.map((m, i) => {
              const product = PRODUCTS[i];
              return (
                <Reveal key={m.name} delay={i * 0.1}>
                  <div className="group relative border border-border bg-card overflow-hidden h-full flex flex-col hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]">
                    <span aria-hidden="true" className="absolute top-0 left-0 z-20 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
                    {product && (
                      <div className="relative aspect-[4/3] bg-charcoal overflow-hidden">
                        <img src={product.image} alt={m.name} className={`h-full w-full group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] ${product.fit === "contain" ? "object-contain bg-white p-3" : "object-cover"}`} />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    )}
                    <div className="p-8 flex-1 flex flex-col">
                      <h3 className="font-display font-extrabold text-2xl text-ink group-hover:text-flame transition-colors">{m.name}</h3>
                      <div className="kicker text-flame mt-2">{m.focus}</div>
                      {product && (
                        <Link to={`/products/${product.slug}`} className="mt-6 inline-flex items-center gap-2 text-ink font-semibold hover:text-flame transition-colors">
                          View product <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal>
            <div className="mt-8 border border-dashed border-flame/40 bg-flame/5 p-10 flex flex-col md:flex-row items-center gap-6 justify-between">
              <div className="flex items-center gap-5">
                <PlusCircle className="h-10 w-10 text-flame flex-shrink-0" />
                <div>
                  <h3 className="font-display font-bold text-xl text-ink">More manufacturers, rentals & managed programs are on the way.</h3>
                  <p className="text-muted-foreground mt-1">As we grow, we add new manufacturers, product lines, rental offerings, and managed inventory programs — always keeping our curated standard.</p>
                </div>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-ink text-ink px-5 py-3 font-semibold hover:bg-ink hover:text-white transition-colors whitespace-nowrap">
                Partner with us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
